import React, { useState, useEffect } from 'react';
import { API } from '../App';

/**
 * Optimized image component that handles:
 * - Proper URL formatting for API images
 * - Error handling with fallback
 * - Quick fallback for invalid URLs
 */
const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  objectFit = 'cover',
  fallbackIcon = null,
  fallbackClassName = 'bg-gradient-to-br from-slate-100 to-slate-200',
  ...props 
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Process the image URL
  const getProcessedUrl = (url) => {
    if (!url) return null;
    
    // Skip invalid URLs immediately
    if (url.includes('example.com')) return null;
    
    // Handle API-prefixed URLs
    if (url.startsWith('/api')) {
      return `${API}${url.replace('/api', '')}`;
    }
    // Handle relative URLs starting with /uploads (old format without /api prefix)
    if (url.startsWith('/uploads')) {
      return `${API}${url}`;
    }
    // Handle full URLs
    if (url.startsWith('http')) {
      return url;
    }
    return url;
  };

  const processedSrc = getProcessedUrl(src);

  // Reset state when src changes
  useEffect(() => {
    setHasError(false);
    setIsLoading(true);
  }, [src]);

  const handleError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  // Determine object-fit class
  const objectFitClass = objectFit === 'contain' ? 'object-contain' : 'object-cover';

  // If no source or error, show fallback immediately
  if (!processedSrc || hasError) {
    return (
      <div className={`flex items-center justify-center ${fallbackClassName} ${className}`} {...props}>
        {fallbackIcon || (
          <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )}
      </div>
    );
  }

  return (
    <img
      src={processedSrc}
      alt={alt}
      className={`${objectFitClass} ${className}`}
      onError={handleError}
      onLoad={handleLoad}
      loading="lazy"
    />
  );
};

export default OptimizedImage;
